import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, User, Crown, Sparkles, Tag, Check, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useRegion } from '@/context/RegionContext';
import GoogleSignInButton from '@/components/GoogleSignInButton';
import { PLAN_PRICES } from '@/lib/plans';
import { trackEvent } from '@/lib/metaPixel';
import AppNav from '@/components/AppNav';
import SEO from '@/components/SEO';

const API = process.env.REACT_APP_BACKEND_URL;

const PLANS = {
  basic: {
    label: 'Basic',
    priceKey: 'basic',
    period: '/month',
    blurb: '5 generations per day. Cancel anytime.',
    icon: <Sparkles className="w-5 h-5" />,
  },
  pro: {
    label: 'Pro',
    priceKey: 'pro',
    period: '/month',
    blurb: '15 generations per day + script re-spin. Cancel anytime.',
    icon: <Crown className="w-5 h-5" />,
  },
};

const RegisterPage = () => {
  const { register, getAuthHeader } = useAuth();
  const { formatPrice, setRegion } = useRegion();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Optional discount code (collapsible)
  const [showDiscount, setShowDiscount] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [discountState, setDiscountState] = useState({
    status: 'idle', // 'idle' | 'checking' | 'valid' | 'invalid'
    message: '',
  });
  const discountTimerRef = useRef(null);

  // If the visitor landed via an affiliate referral link (?ref=CODE), we
  // surface a banner telling them they'll automatically get 50% off their
  // first month. The actual coupon is applied server-side at checkout time.
  const [referral, setReferral] = useState(null); // {code, affiliate_name}

  const planParam = (searchParams.get('plan') || '').toLowerCase();
  const selectedPlan = PLANS[planParam] ? planParam : null;
  const regionParam = (searchParams.get('region') || '').toLowerCase();
  const lockedRegion = (regionParam === 'us' || regionParam === 'uk') ? regionParam : null;

  // If a ?region=us|uk query param was passed (typically from /us or /uk
  // Meta-ad landing pages), lock the region in context so all currency and
  // copy throughout the signup flow honours it.
  useEffect(() => {
    if (lockedRegion) {
      setRegion(lockedRegion, { persistToServer: false });
    }
  }, [lockedRegion, setRegion]);
  const plan = selectedPlan ? PLANS[selectedPlan] : null;

  // Pre-fill discount code from ?discount=XYZ
  useEffect(() => {
    const dc = searchParams.get('discount');
    if (dc) {
      setDiscountCode(dc.toUpperCase());
      setShowDiscount(true);
    }
  }, [searchParams]);

  // Store referral code from URL + resolve the affiliate's name for the banner.
  useEffect(() => {
    const refFromUrl = searchParams.get('ref');
    if (refFromUrl) localStorage.setItem('kylo_ref_code', refFromUrl.toUpperCase());

    const code = (localStorage.getItem('kylo_ref_code') || '').toUpperCase();
    if (!code) return;
    let cancelled = false;
    axios
      .get(`${API}/api/affiliates/validate/${encodeURIComponent(code)}`)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.valid) {
          setReferral({ code, affiliate_name: res.data.affiliate_name || 'a Poettik creator' });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  // Debounced live validation of the discount code
  useEffect(() => {
    if (discountTimerRef.current) clearTimeout(discountTimerRef.current);
    const code = discountCode.trim();
    if (!code) {
      setDiscountState({ status: 'idle', message: '' });
      return undefined;
    }
    setDiscountState({ status: 'checking', message: 'Checking…' });
    discountTimerRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/api/discount-codes/validate`, { code });
        if (res.data?.valid) {
          setDiscountState({ status: 'valid', message: res.data.message });
        } else {
          setDiscountState({ status: 'invalid', message: res.data?.message || 'Invalid code' });
        }
      } catch (err) {
        setDiscountState({ status: 'invalid', message: 'Could not check code' });
      }
    }, 450);
    return () => clearTimeout(discountTimerRef.current);
  }, [discountCode]);

  const goToCheckout = async () => {
    try {
      const affiliateCode = localStorage.getItem('kylo_ref_code') || null;
      const res = await axios.post(
        `${API}/api/payments/create-checkout`,
        {
          origin_url: window.location.origin,
          plan_type: selectedPlan,
          affiliate_code: affiliateCode,
        },
        { headers: getAuthHeader() }
      );
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start checkout. You can try again from the Pricing page.');
      navigate('/pricing');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    // Block submission if the user typed a code but it's invalid
    if (discountCode.trim() && discountState.status === 'invalid') {
      toast.error(discountState.message || 'Discount code is invalid');
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password, discountCode.trim() || null, lockedRegion);
      trackEvent('CompleteRegistration', {
        content_name: selectedPlan ? (PLANS[selectedPlan]?.label || selectedPlan) : 'Free trial',
        status: selectedPlan ? 'paid_intent' : 'free',
      });
      if (selectedPlan) {
        toast.success('Account created! Taking you to checkout…');
        await goToCheckout();
      } else {
        trackEvent('Lead', { content_name: 'Free trial' });
        const okMsg = discountState.status === 'valid'
          ? `Account created! Your ${discountState.message} will apply when you upgrade.`
          : 'Account created! Your 3-day free trial has started.';
        toast.success(okMsg);
        navigate('/creator-dashboard');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="register-page">
      <SEO title="Create Account" description="Create a free Poettik account and start a 3-day free trial. Generate viral hooks and scripts for TikTok Shop affiliates." />
      <nav className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center h-16">
          <AppNav />
        </div>
      </nav>

      <div className="flex-1 flex items-start sm:items-center justify-center px-4 py-10 pb-32">
        <Card className="w-full max-w-md" data-testid="register-card">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">Create Account</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {selectedPlan
                ? `Sign up to continue to ${plan.label} checkout`
                : 'Start your 3-day free trial'}
            </p>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <GoogleSignInButton label="Sign up with Google" discountCode={discountCode} />
            </div>
            <div className="relative mb-5">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-card px-2 text-muted-foreground">or with email</span></div>
            </div>
            {/* Affiliate referral banner — auto-applied 50% off first month */}
            {referral && (
              <div
                className="mb-5 p-3.5 rounded-xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 flex items-start gap-3"
                data-testid="affiliate-referral-banner"
              >
                <div className="w-9 h-9 rounded-lg bg-emerald-500/20 text-emerald-400 flex items-center justify-center shrink-0 text-lg">
                  🎁
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-emerald-300">
                    30% off your first month
                  </p>
                  <p className="text-xs text-muted-foreground leading-snug mt-0.5">
                    Referred by{' '}
                    <span className="text-foreground font-medium">{referral.affiliate_name}</span>{' '}
                    (<span className="font-mono text-emerald-400">{referral.code}</span>).
                    Discount auto-applies at checkout.
                  </p>
                </div>
              </div>
            )}

            {/* Plan summary card */}
            {selectedPlan && (
              <div
                className="mb-5 p-4 rounded-xl bg-primary/5 border border-primary/20 flex items-center gap-3"
                data-testid={`register-plan-summary-${selectedPlan}`}
              >
                <div className="w-10 h-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
                  {plan.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-1.5">
                    <p className="font-semibold text-sm">{plan.label}</p>
                    <p className="font-display text-base font-bold">{formatPrice(PLAN_PRICES[plan.priceKey])}</p>
                    <p className="text-xs text-muted-foreground">{plan.period}</p>
                  </div>
                  <p className="text-xs text-muted-foreground leading-snug truncate">{plan.blurb}</p>
                </div>
                <Link to="/pricing" className="text-xs text-primary hover:underline shrink-0">
                  Change
                </Link>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    className="pl-10"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder="Your name"
                    data-testid="register-name"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="email"
                    className="pl-10"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="you@example.com"
                    data-testid="register-email"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="password"
                    className="pl-10"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Min 6 characters"
                    data-testid="register-password"
                  />
                </div>
              </div>

              {/* Optional discount code */}
              <div className="space-y-2">
                {!showDiscount ? (
                  <button
                    type="button"
                    onClick={() => setShowDiscount(true)}
                    className="text-xs text-primary hover:underline inline-flex items-center gap-1.5"
                    data-testid="register-discount-toggle"
                  >
                    <Tag className="w-3.5 h-3.5" />
                    Got a discount code?
                  </button>
                ) : (
                  <>
                    <Label className="flex items-center gap-1.5">
                      <Tag className="w-3.5 h-3.5" />
                      Discount code
                    </Label>
                    <div className="relative">
                      <Input
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        placeholder="e.g. LAUNCH20"
                        className="pr-10 uppercase tracking-wider"
                        data-testid="register-discount-input"
                        autoComplete="off"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {discountState.status === 'checking' && (
                          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                        )}
                        {discountState.status === 'valid' && (
                          <Check className="w-4 h-4 text-emerald-500" data-testid="register-discount-valid" />
                        )}
                        {discountState.status === 'invalid' && (
                          <X className="w-4 h-4 text-red-500" data-testid="register-discount-invalid" />
                        )}
                      </div>
                    </div>
                    {discountState.message && (
                      <p
                        className={`text-xs ${
                          discountState.status === 'valid'
                            ? 'text-emerald-500'
                            : discountState.status === 'invalid'
                              ? 'text-red-500'
                              : 'text-muted-foreground'
                        }`}
                        data-testid="register-discount-message"
                      >
                        {discountState.status === 'valid' ? '✓ ' : ''}
                        {discountState.message}
                        {discountState.status === 'valid' && ' — applied at checkout'}
                      </p>
                    )}
                  </>
                )}
              </div>

              <Button
                type="submit"
                variant="premium"
                className="w-full"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {selectedPlan ? `Continue to ${plan.label} — ${formatPrice(PLAN_PRICES[plan.priceKey])}${plan.period}` : 'Start Free Trial'}
              </Button>

              <p className="text-xs text-center text-muted-foreground">
                By creating an account you agree to our{' '}
                <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link> and{' '}
                <Link to="/cookies" className="text-primary hover:underline">Cookie Policy</Link>
              </p>
            </form>

            <p className="text-center text-sm text-muted-foreground mt-6">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline font-medium">Sign in</Link>
            </p>

            {!selectedPlan && (
              <p className="text-center text-xs text-muted-foreground mt-4">
                Want to skip the trial?{' '}
                <Link to="/pricing" className="text-primary hover:underline">
                  Choose Basic or Pro
                </Link>
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RegisterPage;
